import type { LocationQuery } from '#vue-router'

/**
 * Injects the Google Gtag base script into the head.
 *
 * @param isEnabled Whether Gtag is enabled.
 * @param id The Gtag client ID.
 * @returns An object with the script contents as one of the keys.
 */
export function injectGtagBase({
  isEnabled,
  id,
}: {
  isEnabled: boolean
  id: string
}) {
  if (!isEnabled) {
    return
  }

  return {
    key: 'google-gtag-base',
    type: 'text/javascript' as const,
    innerHTML: injectGtagBaseScript(id),
  }
}

/**
 * Injects the Gtag base script, but attaches an onerror no-op.
 *
 * @param id The GTAG Id.
 * @returns The script to inject.
 */
function injectGtagBaseScript(id: string): string {
  /* v8 ignore start */
  return `
  (function() {
    var script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=${id}';
    script.onerror = function() {};
    document.head.appendChild(script);
  })();
  `
  /* v8 ignore end */
}

/**
 * Injects the Google GTAG script into the head.
 *
 * __dangerouslyDisableSanitizersByTagID is required to tell Nuxt to not escape
 * characters in this block.
 *
 * @param isEnabled Whether Gtag is enabled.
 * @param analyticsId The Google Analytics client ID.
 * @param adsId The Google Ads client ID.
 * @param userId The user ID.
 * @param userEmail The user email.
 * @param query The query parameters.
 * @returns The Gtag script object.
 */
export function injectGtag({
  isEnabled,
  analyticsId,
  adsId,
  userId,
  userEmail,
  query,
}: {
  isEnabled: boolean
  analyticsId: string
  adsId: string
  userId: string
  userEmail: string
  query: LocationQuery
}) {
  if (!isEnabled) {
    return
  }

  return {
    key: 'google-gtag',
    type: 'text/javascript' as const,
    innerHTML: injectGtagScript({ analyticsId, adsId, userId, userEmail, query }),
  }
}

/**
 * Generates and returns the actual script content for Google Gtag to be
 * injected.
 *
 * @param analyticsId The Google Analytics client ID.
 * @param adsId The Google Ads client ID.
 * @param userId The user ID.
 * @param userEmail The user email.
 * @param query The query parameters.
 * @returns The Gtag script content.
 */
function injectGtagScript({
  analyticsId,
  adsId,
  userId,
  userEmail,
  query,
}: {
  analyticsId: string
  adsId: string
  userId: string
  userEmail: string
  query: LocationQuery
}) {
  /* v8 ignore start */
  return `
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    dataLayer.push(arguments);
  };
  window.gtag('set', 'linker', {'domains': ['rvezy.com']});
  window.gtag('js', new Date());
  window.gtag('set', 'user_data', {
    'user_id': '${userId ?? ''}',
    'email': '${userEmail ?? ''}'
  });
  window.gtag('config', '${analyticsId}', {
    'user_id': '${userId ?? ''}',
    'campaign_id' : '${query?.rvz_camp_id ?? ''}', // rvz_camp_id
    'user_properties': {
      'user_id_dimension': '${userId ?? ''}'
    }
  });
  window.gtag('config', '${adsId}', {
    'allow_enhanced_conversions': true
  });
  `
  /* v8 ignore end */
}
